import React, { useCallback, useEffect, useState } from 'react';
import { App, Button, Input, Select, Space, Tree, Popconfirm } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, AppstoreAddOutlined } from '@ant-design/icons';
import useComponentTemplatesApi from 'pages/liftlogic/v2/Components/useComponentTemplatesApi';
import useComponentTypesApi from 'pages/liftlogic/v2/Components/useComponentTypesApi';
import { buildComponentTree } from 'pages/liftlogic/v2/Components/buildComponentTree';

/** Extrae todos los keys del treeData de forma recursiva. */
function collectKeys(nodes) {
  const keys = [];
  for (const node of nodes) {
    keys.push(node.key);
    if (node.children?.length) keys.push(...collectKeys(node.children));
  }
  return keys;
}

/**
 * Input controlado que expone su valor actual vía ref.
 * Usado en modales modal.confirm para evitar closures frágiles (M4).
 */
function ModalInput({ defaultValue = '', placeholder, valueRef }) {
  const [value, setValue] = useState(defaultValue);
  // Sincronizar el ref en cada cambio para que onOk lea el valor actual.
  valueRef.current = value;
  return (
    <Input
      placeholder={placeholder}
      value={value}
      onChange={(e) => {
        setValue(e.target.value);
        valueRef.current = e.target.value;
      }}
      autoFocus
    />
  );
}

// Gestiona plantillas GLOBALES (liftlogic super-tenant). No hay override por-tenant (YAGNI — decisión 2026-06-19).
function ComponentTemplatesContent() {
  const api = useComponentTemplatesApi();
  const typesApi = useComponentTypesApi();
  const { message, modal } = App.useApp();
  const [roots, setRoots] = useState([]);
  const [rootId, setRootId] = useState(null);
  const [treeData, setTreeData] = useState([]);
  const [types, setTypes] = useState([]); // tipos de componente para el Select al crear nodo
  const [loading, setLoading] = useState(false);
  // M3: expandedKeys controlado — se inicializa con todos los keys al cargar el árbol.
  const [expandedKeys, setExpandedKeys] = useState([]);

  const loadRoots = useCallback(async () => {
    try {
      const data = await api.listRoots();
      setRoots(data.roots || []);
      setRootId((prev) => prev ?? data.roots?.[0]?.id ?? null);
    } catch (e) {
      message.error('No se pudo cargar la lista de plantillas');
    }
  }, [api, message]);

  const loadTree = useCallback(
    async (id) => {
      if (!id) {
        setTreeData([]);
        return;
      }
      setLoading(true);
      try {
        const data = await api.getTree(id);
        const tree = buildComponentTree(data.tree);
        setTreeData(tree);
        // M3: expandir todos los nodos al cargar o recargar el árbol.
        setExpandedKeys(collectKeys(tree));
      } catch (e) {
        message.error('No se pudo cargar la plantilla');
      } finally {
        setLoading(false);
      }
    },
    [api, message]
  );

  const loadTypes = useCallback(async () => {
    try {
      const data = await typesApi.list();
      setTypes(data.types || []);
    } catch (e) {
      /* tipos opcionales */
    }
  }, [typesApi]);

  useEffect(() => {
    loadRoots();
    loadTypes();
  }, [loadRoots, loadTypes]);
  useEffect(() => {
    loadTree(rootId);
  }, [rootId, loadTree]);

  const newRoot = () => {
    // M4: ref en lugar de closure para leer el valor al confirmar.
    const nameRef = { current: '' };
    modal.confirm({
      title: 'Nueva plantilla',
      content: <ModalInput placeholder="Ascensor tracción" valueRef={nameRef} />,
      onOk: async () => {
        const r = await api.createRoot(nameRef.current || 'Nueva plantilla');
        await loadRoots();
        setRootId(r.id);
      },
    });
  };
  const addChild = (parent) => {
    const nameRef = { current: '' };
    const nodeTypeRef = { current: null };
    modal.confirm({
      title: `Agregar bajo "${parent?.title || 'raíz'}"`,
      content: (
        <Space direction="vertical" style={{ width: '100%' }}>
          <ModalInput placeholder="Motor de izaje" valueRef={nameRef} />
          {types.length > 0 && (
            <Select
              allowClear
              placeholder="Tipo de componente (opcional)"
              style={{ width: '100%' }}
              options={types.map((t) => ({ value: t.key, label: t.label }))}
              onChange={(v) => {
                nodeTypeRef.current = v ?? null;
              }}
            />
          )}
        </Space>
      ),
      onOk: async () => {
        await api.addNode(rootId, {
          name: nameRef.current || 'Nuevo',
          parent_id: parent ? parent.key : rootId,
          ...(nodeTypeRef.current != null ? { node_type: nodeTypeRef.current } : {}),
        });
        loadTree(rootId);
      },
    });
  };
  const renameNode = (node) => {
    const nameRef = { current: node.title };
    modal.confirm({
      title: 'Renombrar',
      content: <ModalInput defaultValue={node.title} valueRef={nameRef} />,
      onOk: async () => {
        await api.updateNode(node.key, { name: nameRef.current });
        loadTree(rootId);
      },
    });
  };
  const deleteNode = async (node) => {
    try {
      await api.removeNode(node.key);
      message.success('Eliminado');
      loadTree(rootId);
    } catch (e) {
      message.error('No se pudo eliminar');
    }
  };

  const onDrop = async (info) => {
    // Reparent: el nodo arrastrado pasa a ser hijo del nodo destino (o hermano).
    const dragKey = info.dragNode.key;
    const dropKey = info.node.key;
    const newParent = info.dropToGap ? info.node.raw.parent_id ?? rootId : dropKey;
    try {
      await api.updateNode(dragKey, { parent_id: newParent });
      loadTree(rootId);
    } catch (e) {
      message.error('No se pudo mover');
    }
  };

  const titleRender = (node) => (
    <Space>
      <span>{node.title}</span>
      {node.raw?.node_type && (
        <span style={{ opacity: 0.5, fontSize: 12 }}>[{node.raw.node_type}]</span>
      )}
      <Button
        size="small"
        type="text"
        icon={<AppstoreAddOutlined />}
        title="Agregar hijo"
        onClick={(e) => {
          e.stopPropagation();
          addChild(node);
        }}
      />
      <Button
        size="small"
        type="text"
        icon={<EditOutlined />}
        onClick={(e) => {
          e.stopPropagation();
          renameNode(node);
        }}
      />
      <Popconfirm title="¿Eliminar nodo y descendientes?" onConfirm={() => deleteNode(node)}>
        <Button
          size="small"
          type="text"
          danger
          icon={<DeleteOutlined />}
          onClick={(e) => e.stopPropagation()}
        />
      </Popconfirm>
    </Space>
  );

  return (
    <>
      <Space style={{ marginBottom: 12 }} wrap>
        <Select
          style={{ minWidth: 240 }}
          placeholder="Elegí una plantilla"
          value={rootId}
          onChange={setRootId}
          options={roots.map((r) => ({
            value: r.id,
            label: r.name + (r.tenantId == null ? ' (global)' : ''),
          }))}
        />
        <Button icon={<PlusOutlined />} onClick={newRoot}>
          Nueva plantilla
        </Button>
        {rootId && (
          <Button icon={<AppstoreAddOutlined />} onClick={() => addChild(null)}>
            Agregar nodo raíz
          </Button>
        )}
      </Space>
      <Tree
        draggable
        blockNode
        treeData={treeData}
        expandedKeys={expandedKeys}
        onExpand={setExpandedKeys}
        onDrop={onDrop}
        titleRender={titleRender}
        disabled={loading}
      />
    </>
  );
}

export default ComponentTemplatesContent;
